import React, { useEffect, useState, useCallback } from "react";
import { fetchMyApplications, decideApplication, fetchInbox, decideInbox, deleteInboxMessage, fetchDmThreads, markDmThreadRead, decideReportItem } from "../lib/api";
import { SessionInviteCard } from "./SessionInviteCard";
import { useAuth } from "../context/AuthContext";
import { useUserHub } from "../hooks/useUserHub";
import { AvatarChip } from "./AvatarChip";
import { DmModal } from "./DmModal";

const STATUS_STYLES = {
  pending: "bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40",
  accepted: "bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40",
  rejected: "bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40",
  declined: "bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40",
};

// Compact inbox embedded inside ProfilePanel. Same logic as the old InboxSection
// but stripped of section chrome so it lives inside a tab.
export function InboxPanel({ onToast }) {
  const { user, refreshInboxCount } = useAuth();
  const [data, setData] = useState({ owned_leagues: [], inbox: [], sent: [] });
  const [feed, setFeed] = useState([]);
  const [threads, setThreads] = useState([]);
  const [dmTarget, setDmTarget] = useState(null);
  const [tab, setTab] = useState("invites");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [busyId, setBusyId] = useState("");

  // Phase 59 — load pending + decided history together so the user
  // can clean up their inbox via the new soft-delete button. Three
  // visibility filters: ALL / PENDING / DONE (accepted+declined+other).
  const [inviteFilter, setInviteFilter] = useState("pending");

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const [apps, pendingFeed, decidedFeed, dms] = await Promise.all([
        fetchMyApplications().catch(() => ({ owned_leagues: [], inbox: [], sent: [] })),
        fetchInbox("pending").catch(() => []),
        // The list endpoint accepts a single status param. We pull
        // accepted + declined sequentially-ish; cheap enough at the
        // typical inbox size and avoids a backend shape change.
        Promise.all([
          fetchInbox("accepted").catch(() => []),
          fetchInbox("declined").catch(() => []),
          fetchInbox("final").catch(() => []),
        ]).then((parts) => parts.flat()),
        fetchDmThreads().catch(() => []),
      ]);
      setData(apps);
      // Merge while keeping newest first.
      const merged = [...pendingFeed, ...decidedFeed].sort(
        (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
      );
      setFeed(merged);
      setThreads(dms);
    } catch {
      /* 401 silent */
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);
  // Poll DM threads while panel is mounted so new messages surface without
  // a reload. The WebSocket below carries the live signal; this is just a
  // resilient fallback that converges if the socket drops momentarily.
  useEffect(() => {
    if (!user) return;
    const t = setInterval(() => {
      fetchDmThreads().then(setThreads).catch(() => {});
    }, 20000);
    return () => clearInterval(t);
  }, [user]);

  // Real-time fan-out: a new DM addressed to me fires `dm:new` over the
  // per-user WebSocket. Refresh the threads list immediately so the
  // preview, unread badge, and ordering update without waiting on the poll.
  useUserHub(useCallback((evt) => {
    if (!user) return;
    if (evt?.type === "dm:new") {
      fetchDmThreads().then(setThreads).catch(() => {});
      refreshInboxCount?.();
    }
  }, [user, refreshInboxCount]));

  if (!user) return null;

  const decide = async (app, action) => {
    setBusyId(app.application_id);
    try {
      await decideApplication(app.application_id, action);
      onToast?.(action === "accept"
        ? `Added ${app.user_name} to ${app.league_name || "your league"}.`
        : `${app.user_name} rejected.`);
      await load();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Decision failed.");
    } finally {
      setBusyId("");
    }
  };

  const decideFeedItem = async (item, action) => {
    setBusyId(item.id);
    try {
      const r = await decideInbox(item.id, action);
      if (item.kind === "team_join_invite" && action === "accept") {
        onToast?.(`🛡 Joined ${r.team_name || "the team"}.`);
      } else if (item.kind === "team_join_request" && action === "accept") {
        onToast?.(`🛡 Approved — ${r.team_name || "team"} has a new member.`);
      } else if (item.kind === "league_join_invite" && action === "accept") {
        onToast?.(`🏟 Joined ${r.league_name || "the league"}.`);
      } else if (item.kind === "hq_league_invite" && action === "accept") {
        onToast?.(`🏰 Team joined ${r.league_name || "the league"}.`);
      } else if (item.kind === "team_challenge_invite" && action === "accept") {
        onToast?.("⚡ Challenge accepted — game is on the league calendar.");
      } else {
        onToast?.(action === "accept" ? "✅ Accepted." : "❌ Declined.");
      }
      await load();
      refreshInboxCount?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Decision failed.");
    } finally { setBusyId(""); }
  };

  // Soft-delete a DECIDED inbox row (status != pending). Hides it from
  // the user's feed without losing audit history.
  const deleteFeedItem = async (item) => {
    if (item.kind === "team_challenge_invite") {
      onToast?.("Challenge invites can't be hidden — accept or decline first.");
      return;
    }
    if (item.status === "pending") {
      onToast?.("Decide on this invite (accept/decline) before hiding it.");
      return;
    }
    if (!window.confirm("Hide this message from your inbox? It will be removed from the list.")) return;
    setBusyId(item.id);
    try {
      await deleteInboxMessage(item.id);
      onToast?.("🗑 Message hidden.");
      await load();
      refreshInboxCount?.();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Couldn't delete.");
    } finally { setBusyId(""); }
  };

  const inbox = data.inbox.filter((a) => !statusFilter || a.status === statusFilter);
  const ownedCount = data.owned_leagues.length;
  const pendingCount = data.inbox.filter((a) => a.status === "pending").length;
  const inviteCount = feed.length;
  const dmUnreadTotal = threads.reduce((n, t) => n + (t.unread_count || 0), 0);

  const openDmThread = async (t) => {
    setDmTarget(t.other);
    // Optimistically mark read so the badge clears.
    try {
      await markDmThreadRead(t.other.user_id);
      setThreads((prev) => prev.map((x) =>
        x.pair_key === t.pair_key ? { ...x, unread_count: 0 } : x
      ));
      refreshInboxCount?.();
    } catch { /* non-fatal */ }
  };

  return (
    <div data-testid="profile-inbox-panel" className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs tracking-widest text-[#14b8a6]">
          OPERATOR INBOX
          {inviteCount > 0 && (
            <span
              data-testid="profile-inbox-invite-badge"
              className="ml-2 inline-block animate-glow-pulse bg-[#14b8a6] text-black font-bold px-2 py-0.5 rounded-full text-[10px]"
            >
              {inviteCount} INVITE{inviteCount === 1 ? "" : "S"}
            </span>
          )}
          {pendingCount > 0 && (
            <span
              data-testid="profile-inbox-pending-badge"
              className="ml-2 inline-block animate-glow-pulse bg-[#14b8a6] text-black font-bold px-2 py-0.5 rounded-full text-[10px]"
            >
              {pendingCount} APP{pendingCount === 1 ? "" : "S"}
            </span>
          )}
        </p>
        <p className="text-[11px] text-white/50">
          Team invites, challenges, and league applications all in one place.
        </p>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setTab("invites")}
          data-testid="profile-inbox-tab-invites"
          className={`px-4 py-2 rounded-2xl text-xs font-bold tracking-wider ${
            tab === "invites" ? "btn-neon" : "btn-ghost"
          }`}
        >
          INVITES ({inviteCount})
        </button>
        <button
          onClick={() => setTab("messages")}
          data-testid="profile-inbox-tab-messages"
          className={`relative px-4 py-2 rounded-2xl text-xs font-bold tracking-wider ${
            tab === "messages" ? "btn-neon" : "btn-ghost"
          }`}
        >
          📬 MESSAGES ({threads.length})
          {dmUnreadTotal > 0 && (
            <span
              data-testid="profile-inbox-dm-unread"
              className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#14b8a6] text-white text-[9px] font-bold flex items-center justify-center shadow-[0_0_8px_rgba(20, 184, 166,0.6)]"
            >
              {dmUnreadTotal > 99 ? "99+" : dmUnreadTotal}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab("inbox")}
          data-testid="profile-inbox-tab-applications"
          className={`px-4 py-2 rounded-2xl text-xs font-bold tracking-wider ${
            tab === "inbox" ? "btn-neon" : "btn-ghost"
          }`}
        >
          APPLICATIONS ({(data.inbox || []).filter((a) => a.status === "pending").length})
        </button>
        <button
          onClick={() => setTab("sent")}
          data-testid="profile-inbox-tab-sent"
          className={`px-4 py-2 rounded-2xl text-xs font-bold tracking-wider ${
            tab === "sent" ? "btn-neon" : "btn-ghost"
          }`}
        >
          SENT ({data.sent.length})
        </button>
        {tab === "inbox" && (
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="profile-inbox-status-filter"
            className="bg-black/50 border border-[#14b8a6]/30 rounded-2xl px-3 py-2 text-white text-xs"
          >
            <option value="">ALL</option>
            <option value="pending">PENDING</option>
            <option value="accepted">ACCEPTED</option>
            <option value="rejected">REJECTED</option>
          </select>
        )}
      </div>

      {tab === "invites" ? (
        <>
          {/* Phase 59 — visibility filter so the user can see decided
              messages and clean them up via the new trash button. */}
          <div className="flex gap-1.5 mb-3" data-testid="invites-filter">
            {[
              { id: "pending", label: "PENDING" },
              { id: "done",    label: "DONE"    },
              { id: "all",     label: "ALL"     },
            ].map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setInviteFilter(f.id)}
                data-testid={`invites-filter-${f.id}`}
                className={`text-[10px] tracking-widest px-3 py-1.5 rounded-lg border ${
                  inviteFilter === f.id
                    ? "bg-[#14b8a6]/20 text-[#14b8a6] border-[#14b8a6]/55"
                    : "bg-black/40 text-white/65 border-white/15 hover:border-[#14b8a6]/40"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          {(() => {
            const visible = feed.filter((m) => {
              if (inviteFilter === "all") return true;
              if (inviteFilter === "pending") return m.status === "pending";
              return m.status && m.status !== "pending";
            });
            if (visible.length === 0) {
              return (
                <div className="glass rounded-2xl p-6 text-center" data-testid="invites-empty">
                  <p className="text-sm text-white/70">
                    {inviteFilter === "pending"
                      ? "No pending invites."
                      : inviteFilter === "done"
                        ? "No past messages."
                        : "No invites yet."}
                  </p>
                  <p className="text-xs text-white/50 mt-1">
                    Team managers can send you join invites here, and rival teams can challenge yours to a match.
                  </p>
                </div>
              );
            }
            return (
              <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1" data-testid="profile-invites-list">
                {visible.map((m) => (
                  <InviteCard
                    key={m.id}
                    item={m}
                    busy={busyId === m.id}
                    onDecide={(action) => decideFeedItem(m, action)}
                    onDelete={() => deleteFeedItem(m)}
                  />
                ))}
              </div>
            );
          })()}
        </>
      ) : tab === "messages" ? (
        threads.length === 0 ? (
          <div className="glass rounded-2xl p-6 text-center" data-testid="messages-empty">
            <p className="text-sm text-white/70">No conversations yet.</p>
            <p className="text-xs text-white/50 mt-1">
              When someone DMs you, the thread lands here. Open a player card and tap MSG to start one.
            </p>
          </div>
        ) : (
          <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1" data-testid="profile-messages-list">
            {threads.map((t) => (
              <button
                key={t.pair_key}
                type="button"
                onClick={() => openDmThread(t)}
                data-testid={`profile-thread-${t.other.user_id}`}
                className={`w-full text-left glass rounded-2xl p-3 flex items-center gap-3 transition-colors hover:bg-white/5 ${
                  t.unread_count > 0 ? "border border-[#14b8a6]/40" : ""
                }`}
              >
                <AvatarChip avatarId={t.other.avatar_id} pfpUrl={t.other.pfp_url} size={44} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className={`text-sm truncate ${t.unread_count > 0 ? "font-bold text-white" : "text-white/85"}`}>
                      {t.other.name || "Operator"}
                    </p>
                    <span className="text-[10px] text-white/40 whitespace-nowrap">
                      {new Date(t.last_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                    </span>
                  </div>
                  <p className={`text-[11px] truncate ${t.unread_count > 0 ? "text-[#14b8a6]" : "text-white/55"}`}>
                    {t.last_from_me && <span className="text-white/40 mr-1">You:</span>}
                    {t.last_message}
                  </p>
                </div>
                {t.unread_count > 0 && (
                  <span
                    data-testid={`profile-thread-unread-${t.other.user_id}`}
                    className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-[#14b8a6] text-black text-[10px] font-bold flex items-center justify-center shadow-[0_0_10px_rgba(20, 184, 166,0.7)]"
                  >
                    {t.unread_count > 99 ? "99+" : t.unread_count}
                  </span>
                )}
              </button>
            ))}
          </div>
        )
      ) : tab === "inbox" ? (
        ownedCount === 0 ? (
          <div className="glass rounded-2xl p-6 text-center">
            <p className="text-sm text-white/70">You don't host any leagues yet.</p>
            <p className="text-xs text-white/50 mt-1">
              Save your profile, then create a league to start receiving applicants.
            </p>
          </div>
        ) : inbox.length === 0 ? (
          <div className="glass rounded-2xl p-6 text-center">
            <p className="text-sm text-white/60">
              {statusFilter ? `No ${statusFilter} applications.` : "No applications yet."}
            </p>
          </div>
        ) : (
          <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1" data-testid="profile-inbox-list">
            {inbox.map((a) => (
              <div
                key={a.application_id}
                data-testid={`profile-inbox-card-${a.application_id}`}
                className="glass rounded-2xl p-3 flex items-center gap-3"
              >
                <AvatarChip avatarId={a.user_avatar_id} pfpUrl={a.user_pfp_url} size={44} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-bold text-sm truncate">{a.user_name}</p>
                    <span
                      className={`text-[9px] font-bold tracking-widest px-2 py-0.5 rounded-full ${STATUS_STYLES[a.status] || ""}`}
                    >
                      {a.status.toUpperCase()}
                    </span>
                  </div>
                  <p className="text-[11px] text-[#a3a3ac] truncate">
                    {a.league_name || a.league_id} • MMR {a.user_elo ?? "—"} • {(a.user_type || "casual").toUpperCase()}
                  </p>
                </div>
                {a.status === "pending" && (
                  <div className="flex gap-1.5">
                    <button
                      onClick={() => decide(a, "reject")}
                      disabled={busyId === a.application_id}
                      data-testid={`profile-inbox-reject-${a.application_id}`}
                      className="px-3 py-1.5 rounded-xl text-[11px] bg-black/50 border border-[#14b8a6]/50 text-[#14b8a6] disabled:opacity-50 font-bold"
                    >
                      REJECT
                    </button>
                    <button
                      onClick={() => decide(a, "accept")}
                      disabled={busyId === a.application_id}
                      data-testid={`profile-inbox-accept-${a.application_id}`}
                      className="px-3 py-1.5 rounded-xl text-[11px] btn-neon disabled:opacity-50"
                    >
                      ACCEPT
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )
      ) : data.sent.length === 0 ? (
        <div className="glass rounded-2xl p-6 text-center">
          <p className="text-sm text-white/60">You haven't applied to any leagues yet.</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1" data-testid="profile-inbox-sent-list">
          {data.sent.map((a) => (
            <div
              key={a.application_id}
              data-testid={`profile-sent-card-${a.application_id}`}
              className="glass rounded-2xl p-3 flex items-center justify-between gap-3"
            >
              <div className="min-w-0">
                <p className="font-display text-base grad-text truncate">{a.league_name || a.league_id}</p>
                <p className="text-[11px] text-white/50">
                  Submitted {new Date(a.created_at).toLocaleString()}
                </p>
              </div>
              <span
                className={`text-[10px] font-bold tracking-widest px-2.5 py-1 rounded-full ${STATUS_STYLES[a.status] || ""}`}
              >
                {a.status.toUpperCase()}
              </span>
            </div>
          ))}
        </div>
      )}

      {dmTarget && (
        <DmModal
          targetUser={dmTarget}
          onClose={() => { setDmTarget(null); load(); refreshInboxCount?.(); }}
          onToast={onToast}
        />
      )}
    </div>
  );
}

export default InboxPanel;

// ---------------------------------------------------------------------------
// Card renderers
// ---------------------------------------------------------------------------
function InviteCard({ item, busy, onDecide, onDelete }) {
  if (item.kind === "friend_request") {
    const p = item.payload || {};
    return (
      <div
        data-testid={`invite-card-${item.id}`}
        className="glass rounded-2xl p-3 flex items-center gap-3 border border-[#14b8a6]/30"
      >
        <AvatarChip avatarId={p.from_user_avatar_id} pfpUrl={p.from_user_pfp_url} size={44} />
        <div className="flex-1 min-w-0">
          <p className="text-[9px] tracking-widest text-[#14b8a6] font-bold">
            <i className="fas fa-user-group mr-1" />FRIEND REQUEST
          </p>
          <p className="font-bold text-sm truncate">{p.from_user_name || "An operator"}</p>
          <p className="text-[11px] text-[#a3a3ac]">wants to add you to their community.</p>
        </div>
        <DecideButtons
          busy={busy} item={item}
          onDecide={onDecide} onDelete={onDelete}
          testidBase={`invite-${item.id}`}
        />
      </div>
    );
  }
  if (item.kind === "team_join_request") {
    const p = item.payload || {};
    return (
      <div
        data-testid={`invite-card-${item.id}`}
        className="glass rounded-2xl p-3 flex items-center gap-3 border border-[#14b8a6]/30"
      >
        <AvatarChip avatarId={p.requester_avatar_id} pfpUrl={p.requester_pfp_url} size={44} />
        <div className="flex-1 min-w-0">
          <p className="text-[9px] tracking-widest text-[#14b8a6] font-bold">
            <i className="fas fa-user-plus mr-1" />JOIN REQUEST
          </p>
          <p className="font-bold text-sm truncate">{p.requester_name}</p>
          <p className="text-[11px] text-[#a3a3ac] truncate">
            wants to join {p.team_name}
            {p.requester_elo ? <> · MMR {p.requester_elo}</> : null}
          </p>
        </div>
        <DecideButtons busy={busy} item={item} onDecide={onDecide} onDelete={onDelete} testidBase={`invite-${item.id}`} />
      </div>
    );
  }
  if (item.kind === "team_join_invite") {
    const p = item.payload || {};
    return (
      <div
        data-testid={`invite-card-${item.id}`}
        className="glass rounded-2xl p-3 flex items-center gap-3 border border-[#14b8a6]/30"
      >
        <AvatarChip avatarId={p.team_avatar_id} pfpUrl={p.team_pfp_url} size={44} />
        <div className="flex-1 min-w-0">
          <p className="text-[9px] tracking-widest text-[#14b8a6] font-bold">
            <i className="fas fa-shield-halved mr-1" />TEAM INVITE
          </p>
          <p className="font-bold text-sm truncate">{p.team_name}</p>
          <p className="text-[11px] text-[#a3a3ac] truncate">
            in {p.league_name}
            {p.manager_name && <> · from {p.manager_name}</>}
          </p>
        </div>
        <DecideButtons busy={busy} item={item} onDecide={onDecide} onDelete={onDelete} testidBase={`invite-${item.id}`} />
      </div>
    );
  }
  if (item.kind === "league_join_invite") {
    const p = item.payload || {};
    return (
      <div
        data-testid={`invite-card-${item.id}`}
        className="glass rounded-2xl p-3 flex items-center gap-3 border border-[#14b8a6]/30"
      >
        <AvatarChip avatarId={p.owner_avatar_id} pfpUrl={p.owner_pfp_url} size={44} />
        <div className="flex-1 min-w-0">
          <p className="text-[9px] tracking-widest text-[#14b8a6] font-bold">
            <i className="fas fa-trophy mr-1" />LEAGUE INVITE
          </p>
          <p className="font-bold text-sm truncate">{p.league_name}</p>
          <p className="text-[11px] text-[#a3a3ac] truncate">
            {p.league_game}{p.owner_name && <> · from {p.owner_name}</>}
          </p>
        </div>
        <DecideButtons busy={busy} item={item} onDecide={onDecide} onDelete={onDelete} testidBase={`invite-${item.id}`} />
      </div>
    );
  }
  if (item.kind === "team_challenge_invite") {
    const p = item.payload || {};
    const when = p.scheduled_at ? new Date(p.scheduled_at) : null;
    const whenStr = when ? when.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "TBD";
    return (
      <div
        data-testid={`invite-card-${item.id}`}
        className="glass rounded-2xl p-3 border border-[#14b8a6]/30 space-y-2"
      >
        <p className="text-[9px] tracking-widest text-[#14b8a6] font-bold">
          <i className="fas fa-bolt mr-1" />CHALLENGE
        </p>
        <div className="grid grid-cols-3 items-center gap-2">
          <div className="text-center">
            <AvatarChip avatarId={p.from_team?.avatar_id} pfpUrl={p.from_team?.pfp_url} size={40} />
            <p className="text-xs font-bold truncate mt-1">{p.from_team?.name}</p>
          </div>
          <div className="text-center font-display text-lg grad-text">VS</div>
          <div className="text-center">
            <AvatarChip avatarId={p.to_team?.avatar_id} pfpUrl={p.to_team?.pfp_url} size={40} />
            <p className="text-xs font-bold truncate mt-1">{p.to_team?.name}</p>
          </div>
        </div>
        <p className="text-xs text-[#14b8a6] text-center">
          <i className="fas fa-clock mr-1" />{whenStr}
        </p>
        {p.note && <p className="text-[11px] text-white/60 italic text-center">"{p.note}"</p>}
        <div className="flex justify-end pt-1 border-t border-white/10">
          <DecideButtons busy={busy} item={item} onDecide={onDecide} onDelete={onDelete} testidBase={`invite-${item.id}`} />
        </div>
      </div>
    );
  }
  if (item.kind === "hq_league_invite") {
    const p = item.payload || {};
    return (
      <div
        data-testid={`invite-card-${item.id}`}
        className="glass rounded-2xl p-3 flex items-center gap-3 border border-[#14b8a6]/40"
      >
        <AvatarChip avatarId={p.team_avatar_id} pfpUrl={p.team_pfp_url} size={44} />
        <div className="flex-1 min-w-0">
          <p className="text-[9px] tracking-widest text-[#14b8a6] font-bold">
            <i className="fas fa-shield-halved mr-1" />LEAGUE → TEAM INVITE
          </p>
          <p className="font-bold text-sm truncate">
            {p.league_name}
          </p>
          <p className="text-[11px] text-[#a3a3ac] truncate">
            wants <b className="text-white">{p.team_name}</b> · {p.league_game}
            {p.owner_name && <> · from {p.owner_name}</>}
          </p>
        </div>
        <DecideButtons busy={busy} item={item} onDecide={onDecide} onDelete={onDelete} testidBase={`invite-${item.id}`} />
      </div>
    );
  }
  if (item.kind === "tryout_decision") {
    const p = item.payload || {};
    const approved = item.status === "approved";
    return (
      <div
        data-testid={`invite-card-${item.id}`}
        className={`glass rounded-2xl p-3 flex items-center gap-3 border ${
          approved ? "border-[#14b8a6]/40" : "border-[#14b8a6]/40"
        }`}
      >
        <AvatarChip avatarId={p.team_avatar_id} pfpUrl={p.team_pfp_url} size={40} />
        <div className="flex-1 min-w-0">
          <p className={`text-[9px] tracking-widest font-bold ${approved ? "text-[#14b8a6]" : "text-[#14b8a6]"}`}>
            <i className="fas fa-bullseye mr-1" />TRYOUT {approved ? "ACCEPTED" : "REJECTED"}
          </p>
          <p className="font-bold text-sm truncate">{p.tryout_title}</p>
          <p className="text-[11px] text-[#a3a3ac] truncate">
            {approved ? `Welcome to ${p.team_name}.` : `${p.team_name} declined this round.`}
          </p>
        </div>
      </div>
    );
  }
  if (item.kind === "session_invite") {
    return (
      <SessionInviteCard
        item={item}
        onRsvped={onDecide /* parent uses this to call load() */}
      />
    );
  }
  if (item.kind === "player_report") {
    return <ReportInviteCard item={item} />;
  }
  return null;
}

function DecideButtons({ busy, item, onDecide, onDelete, testidBase }) {
  // Decided rows (accepted / declined / final / similar) get a trash
  // button instead of accept/decline so the user can clean up their
  // inbox. Pending rows show the original accept/decline pair.
  // Challenge invites live in a different collection and can't be
  // soft-hidden — caller-side handler shows a toast explaining that.
  const isDecided = item && item.status && item.status !== "pending";
  const canDelete = isDecided && onDelete && item?.kind !== "team_challenge_invite";
  if (canDelete) {
    return (
      <div className="flex gap-1.5">
        <button
          onClick={onDelete}
          disabled={busy}
          data-testid={`${testidBase}-delete-btn`}
          title="Hide this message"
          aria-label="Hide this message"
          className="w-9 h-9 rounded-xl text-[12px] bg-black/50 border border-white/15 text-white/70 hover:text-white hover:border-red-500/50 hover:bg-red-500/10 disabled:opacity-50"
        >
          <i className="fas fa-trash" />
        </button>
      </div>
    );
  }
  return (
    <div className="flex gap-1.5">
      <button
        onClick={() => onDecide("decline")}
        disabled={busy}
        data-testid={`${testidBase}-decline-btn`}
        className="px-3 py-1.5 rounded-xl text-[11px] bg-black/50 border border-[#14b8a6]/50 text-[#14b8a6] disabled:opacity-50 font-bold"
      >
        DECLINE
      </button>
      <button
        onClick={() => onDecide("accept")}
        disabled={busy}
        data-testid={`${testidBase}-accept-btn`}
        className="px-3 py-1.5 rounded-xl text-[11px] btn-neon disabled:opacity-50"
      >
        ACCEPT
      </button>
    </div>
  );
}

/**
 * ReportInviteCard — surfaced to league owners/managers when one of their
 * league members is reported by another player. Lets staff:
 *   - BAN: remove from a specific league of theirs + add to that league's
 *     banned_user_ids list
 *   - ALLOW: close the item without further action
 *   - MESSAGE: DM the reported player for clarification
 */
function ReportInviteCard({ item }) {
  const p = item.payload || {};
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState("");
  const [mode, setMode] = useState(null);  // "ban" | "message"
  const [chosenLeague, setChosenLeague] = useState((p.leagues || [])[0]?.league_id || "");
  const [draft, setDraft] = useState("");

  const submit = async (action, extra = {}) => {
    if (busy) return;
    setBusy(true);
    setErr("");
    try {
      const r = await decideReportItem(item.id, { action, ...extra });
      setDone(action);
      if (action === "ban") setMode(null);
      if (action === "message") setMode(null);
      // Caller's load() will refresh the feed within the parent's poll loop.
    } catch (e) {
      setErr(e?.response?.data?.detail || "Action failed.");
    } finally { setBusy(false); }
  };

  if (done) {
    const label =
      done === "ban" ? "Player removed from league."
      : done === "allow" ? "Closed — player allowed."
      : done === "message" ? "Clarification DM sent." : "Done.";
    return (
      <div
        data-testid={`invite-card-${item.id}`}
        className="glass rounded-2xl p-3 border border-[#14b8a6]/40 text-[12px] text-[#14b8a6]"
      >
        <i className="fas fa-check mr-1" />{label}
      </div>
    );
  }

  return (
    <div
      data-testid={`invite-card-${item.id}`}
      className="glass rounded-2xl p-3 border border-[#14b8a6]/40 space-y-2"
    >
      <p className="text-[9px] tracking-widest text-[#14b8a6] font-bold">
        <i className="fas fa-flag mr-1" />PLAYER REPORT
      </p>
      <div className="flex items-center gap-2">
        <AvatarChip avatarId={p.reported_avatar_id} pfpUrl={p.reported_pfp_url} size={36} />
        <div className="min-w-0 flex-1">
          <p className="font-bold text-sm truncate">{p.reported_name || p.reported_user_id}</p>
          <p className="text-[11px] text-[#a3a3ac] truncate">
            reported for <b className="text-white">{p.reason_label || p.reason}</b>
            {p.reporter_name && <> · by {p.reporter_name}</>}
          </p>
        </div>
      </div>
      {p.details && (
        <p className="text-[11px] text-white/70 italic bg-black/30 rounded-lg px-2 py-1.5 line-clamp-3">
          "{p.details}"
        </p>
      )}

      {mode === "ban" ? (
        <div className="space-y-2">
          {(p.leagues || []).length > 1 ? (
            <select
              value={chosenLeague}
              onChange={(e) => setChosenLeague(e.target.value)}
              data-testid={`report-ban-league-${item.id}`}
              className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-sm text-white"
            >
              {(p.leagues || []).map((lg) => (
                <option key={lg.league_id} value={lg.league_id}>{lg.name}</option>
              ))}
            </select>
          ) : (
            <p className="text-[11px] text-white/60">
              From <b className="text-white">{(p.leagues || [])[0]?.name || "league"}</b>
            </p>
          )}
          <div className="flex gap-2 justify-end">
            <button type="button" disabled={busy} onClick={() => setMode(null)}
                    className="px-3 py-1.5 rounded-xl text-[10px] tracking-widest btn-ghost">
              CANCEL
            </button>
            <button type="button" disabled={busy || !chosenLeague}
                    onClick={() => submit("ban", { league_id: chosenLeague })}
                    data-testid={`report-confirm-ban-${item.id}`}
                    className="px-3 py-1.5 rounded-xl text-[10px] tracking-widest font-bold bg-[#14b8a6]/15 border border-[#14b8a6]/60 text-[#14b8a6] hover:bg-[#14b8a6]/30">
              CONFIRM BAN
            </button>
          </div>
        </div>
      ) : mode === "message" ? (
        <div className="space-y-2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Ask the player for clarification…"
            maxLength={1500}
            rows={3}
            data-testid={`report-message-draft-${item.id}`}
            className="w-full bg-black/40 border border-white/15 rounded-xl px-3 py-2 text-sm text-white"
          />
          <div className="flex gap-2 justify-end">
            <button type="button" disabled={busy} onClick={() => setMode(null)}
                    className="px-3 py-1.5 rounded-xl text-[10px] tracking-widest btn-ghost">
              CANCEL
            </button>
            <button type="button" disabled={busy || !draft.trim()}
                    onClick={() => submit("message", { message: draft.trim() })}
                    data-testid={`report-send-message-${item.id}`}
                    className="px-3 py-1.5 rounded-xl text-[10px] tracking-widest font-bold btn-neon">
              SEND DM
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5 justify-end pt-1 border-t border-white/10">
          <button type="button" disabled={busy} onClick={() => setMode("message")}
                  data-testid={`report-msg-btn-${item.id}`}
                  className="px-3 py-1.5 rounded-xl text-[10px] tracking-widest bg-[#14b8a6]/15 border border-[#14b8a6]/50 text-[#14b8a6] hover:bg-[#14b8a6]/25">
            <i className="fas fa-envelope mr-1" />MESSAGE
          </button>
          <button type="button" disabled={busy} onClick={() => submit("allow")}
                  data-testid={`report-allow-btn-${item.id}`}
                  className="px-3 py-1.5 rounded-xl text-[10px] tracking-widest bg-[#14b8a6]/15 border border-[#14b8a6]/50 text-[#14b8a6] hover:bg-[#14b8a6]/25">
            <i className="fas fa-check mr-1" />ALLOW
          </button>
          <button type="button" disabled={busy || (p.leagues || []).length === 0}
                  onClick={() => setMode("ban")}
                  data-testid={`report-ban-btn-${item.id}`}
                  className="px-3 py-1.5 rounded-xl text-[10px] tracking-widest bg-[#14b8a6]/15 border border-[#14b8a6]/50 text-[#14b8a6] hover:bg-[#14b8a6]/25 disabled:opacity-40">
            <i className="fas fa-ban mr-1" />BAN
          </button>
        </div>
      )}
      {err && <p data-testid={`report-error-${item.id}`} className="text-[11px] text-[#14b8a6]">{err}</p>}
    </div>
  );
}

